import { useMutation } from '@apollo/client'
import { useState } from 'react'
import { toast } from 'sonner'

import { CHANGE_TRAILER_NUMBER, CHANGE_TRUCK_NUMBER } from '../../../../../../data/mutations/shipment'
import { MY_SHIPMENT } from '../../../../../../data/queries/shipment'
import type { TActiveAssetType } from './eld-types'

interface IUseELDAssetMutationsParams {
  currentTrailerNumber?: string | null
  currentTruckNumber?: string | null
  onTrailerChange?: (trailerNumber: string | null) => void
  onTruckChange?: (truckNumber: string | null) => void
  shipmentID: string
  shipmentOwnerWorkspaceID: string
}

export const useELDAssetMutations = ({
  currentTrailerNumber,
  currentTruckNumber,
  onTrailerChange,
  onTruckChange,
  shipmentID,
  shipmentOwnerWorkspaceID
}: IUseELDAssetMutationsParams) => {
  const [selectingAsset, setSelectingAsset] = useState<string | null>(null)

  const [changeTrailerNumber, { loading: loadingTrailerUpdate }] = useMutation(CHANGE_TRAILER_NUMBER, {
    onCompleted: () => {
      toast.success('Trailer number updated')
      setSelectingAsset(null)
    },
    onError: () => {
      toast.error('Failed to update trailer number')
      setSelectingAsset(null)
    }
  })

  const [changeTruckNumber, { loading: loadingTruckUpdate }] = useMutation(CHANGE_TRUCK_NUMBER, {
    onCompleted: () => {
      toast.success('Truck number updated')
      setSelectingAsset(null)
    },
    onError: () => {
      toast.error('Failed to update truck number')
      setSelectingAsset(null)
    }
  })

  const isLoadingUpdate = loadingTrailerUpdate || loadingTruckUpdate

  const executeAssetUpdate = (
    value: string | null,
    onSuccess?: () => void,
    assetType?: TActiveAssetType,
    activeAssetType?: TActiveAssetType
  ) => {
    const targetAssetType = assetType ?? activeAssetType ?? 'trailer'
    const targetCurrentValue = targetAssetType === 'trailer' ? currentTrailerNumber : currentTruckNumber
    const mutation = targetAssetType === 'trailer' ? changeTrailerNumber : changeTruckNumber
    const fieldName = targetAssetType === 'trailer' ? 'trailerNumber' : 'truckNumber'
    const mutationName = targetAssetType === 'trailer' ? 'changeTrailerNumber' : 'changeTruckNumber'

    const getActionType = (): 'added' | 'removed' | 'replaced' => {
      const hasCurrentValue = !!targetCurrentValue
      const hasNewValue = !!value

      if (hasCurrentValue && hasNewValue && targetCurrentValue !== value) {
        return 'replaced'
      }
      if (hasCurrentValue && !hasNewValue) {
        return 'removed'
      }
      return 'added'
    }

    const actionType = getActionType()

    if (value) {
      setSelectingAsset(value)
    }

    mutation({
      optimisticResponse: {
        [mutationName]: {
          __typename: 'Shipment',
          id: shipmentID,
          [fieldName]: value
        }
      },
      variables: {
        actionType,
        id: shipmentID,
        shipmentOwnerWorkspaceID,
        [fieldName]: value
      },
      update: (cache) => {
        cache.updateQuery(
          {
            query: MY_SHIPMENT,
            variables: { id: shipmentID }
          },
          (existingData) => {
            if (!existingData?.myShipment) return existingData
            return {
              ...existingData,
              myShipment: {
                ...existingData.myShipment,
                [fieldName]: value
              }
            }
          }
        )
      },
      onCompleted: (data) => {
        const responseValue = data?.[mutationName]?.[fieldName] ?? null
        if (targetAssetType === 'trailer') {
          onTrailerChange?.(responseValue)
        } else {
          onTruckChange?.(responseValue)
        }
        onSuccess?.()
      }
    })
  }

  return {
    executeAssetUpdate,
    isLoadingUpdate,
    selectingAsset,
    setSelectingAsset
  }
}
